import React, { useEffect, useRef, useState, useCallback } from 'react';
import { View, StyleSheet, Animated, Easing } from 'react-native';
import { Text, Card, useTheme, IconButton, Chip } from 'react-native-paper';
import type { MasteryState } from '@coffeeplexity/shared';

export interface ProgressUpdate {
  id: string;
  skillId: string;
  skillName: string;
  oldProgress: number;
  newProgress: number;
  confidence: number;
  source:
    | 'user_action'
    | 'ai_inference'
    | 'peer_comparison'
    | 'content_completion';
  timestamp: string;
  optimistic: boolean; // True if this is a predicted update before server confirmation
}

export interface RealTimeProgressUpdaterProps {
  updates: ProgressUpdate[];
  onUpdateConfirmed?: (updateId: string) => void;
  onUpdateRejected?: (updateId: string, reason: string) => void;
  showOptimisticUpdates?: boolean;
  animationDuration?: number;
  maxVisibleUpdates?: number;
  style?: any;
}

export function RealTimeProgressUpdater({
  updates,
  onUpdateConfirmed,
  onUpdateRejected,
  showOptimisticUpdates = true,
  animationDuration = 2000,
  maxVisibleUpdates = 3,
  style,
}: RealTimeProgressUpdaterProps) {
  const theme = useTheme();
  const [visibleUpdates, setVisibleUpdates] = useState<ProgressUpdate[]>([]);
  const [processingQueue, setProcessingQueue] = useState<ProgressUpdate[]>([]);
  const animationRefs = useRef<Map<string, Animated.Value>>(new Map());

  // Process new updates
  useEffect(() => {
    const newUpdates = updates.filter(
      update =>
        !visibleUpdates.some(visible => visible.id === update.id) &&
        !processingQueue.some(queued => queued.id === update.id)
    );

    if (newUpdates.length > 0) {
      setProcessingQueue(prev => [...prev, ...newUpdates]);
    }
  }, [updates, visibleUpdates, processingQueue]);

  // Process queued updates
  useEffect(() => {
    if (
      processingQueue.length > 0 &&
      visibleUpdates.length < maxVisibleUpdates
    ) {
      const nextUpdate = processingQueue[0];

      // Only show optimistic updates if enabled
      if (!nextUpdate.optimistic || showOptimisticUpdates) {
        processNextUpdate(nextUpdate);
      }

      setProcessingQueue(prev => prev.slice(1));
    }
  }, [
    processingQueue,
    visibleUpdates.length,
    maxVisibleUpdates,
    showOptimisticUpdates,
  ]);

  const processNextUpdate = useCallback(
    (update: ProgressUpdate) => {
      // Create animation value for this update
      const animValue = new Animated.Value(0);
      animationRefs.current.set(update.id, animValue);

      // Add to visible updates
      setVisibleUpdates(prev => [...prev, update]);

      // Start entrance animation
      Animated.sequence([
        Animated.timing(animValue, {
          toValue: 1,
          duration: 500,
          easing: Easing.out(Easing.back(1.2)),
          useNativeDriver: true,
        }),
        Animated.delay(animationDuration),
        Animated.timing(animValue, {
          toValue: 0,
          duration: 400,
          easing: Easing.in(Easing.cubic),
          useNativeDriver: true,
        }),
      ]).start(() => {
        // Remove from visible updates after animation
        setVisibleUpdates(prev => prev.filter(u => u.id !== update.id));
        animationRefs.current.delete(update.id);

        // Confirm update if it was optimistic and successful
        if (update.optimistic && onUpdateConfirmed) {
          onUpdateConfirmed(update.id);
        }
      });
    },
    [animationDuration, onUpdateConfirmed]
  );

  const handleUpdateDismiss = useCallback((updateId: string) => {
    const animValue = animationRefs.current.get(updateId);
    if (animValue) {
      Animated.timing(animValue, {
        toValue: 0,
        duration: 300,
        easing: Easing.in(Easing.cubic),
        useNativeDriver: true,
      }).start(() => {
        setVisibleUpdates(prev => prev.filter(u => u.id !== updateId));
        animationRefs.current.delete(updateId);
      });
    }
  }, []);

  const getUpdateIcon = (source: ProgressUpdate['source']) => {
    switch (source) {
      case 'user_action':
        return 'account-check';
      case 'ai_inference':
        return 'brain';
      case 'peer_comparison':
        return 'account-group';
      case 'content_completion':
        return 'book-check';
      default:
        return 'trending-up';
    }
  };

  const getUpdateColor = (update: ProgressUpdate) => {
    const improvement = update.newProgress - update.oldProgress;
    if (improvement > 0.1) return '#4CAF50'; // Significant improvement
    if (improvement > 0.05) return '#8BC34A'; // Moderate improvement
    if (improvement > 0) return '#CDDC39'; // Small improvement
    return '#FF9800'; // No improvement or decline
  };

  const getSourceLabel = (source: ProgressUpdate['source']) => {
    switch (source) {
      case 'user_action':
        return 'Direct Activity';
      case 'ai_inference':
        return 'AI Analysis';
      case 'peer_comparison':
        return 'Peer Learning';
      case 'content_completion':
        return 'Content Mastery';
      default:
        return 'Progress Update';
    }
  };

  const renderProgressUpdate = (update: ProgressUpdate) => {
    const animValue = animationRefs.current.get(update.id);
    if (!animValue) return null;

    const updateColor = getUpdateColor(update);
    const improvement = update.newProgress - update.oldProgress;
    const improvementPercent = Math.round(improvement * 100);

    return (
      <Animated.View
        key={update.id}
        style={[
          styles.updateContainer,
          {
            opacity: animValue,
            transform: [
              {
                translateY: animValue.interpolate({
                  inputRange: [0, 1],
                  outputRange: [-50, 0],
                }),
              },
              {
                scale: animValue.interpolate({
                  inputRange: [0, 1],
                  outputRange: [0.8, 1],
                }),
              },
            ],
          },
        ]}
      >
        <Card
          style={[
            styles.updateCard,
            {
              borderLeftColor: updateColor,
              borderLeftWidth: 4,
            },
          ]}
          mode='elevated'
        >
          <Card.Content style={styles.updateContent}>
            {/* Header */}
            <View style={styles.updateHeader}>
              <View style={styles.updateHeaderLeft}>
                <View
                  style={[
                    styles.updateIconContainer,
                    { backgroundColor: updateColor + '20' },
                  ]}
                >
                  <IconButton
                    icon={getUpdateIcon(update.source)}
                    size={16}
                    iconColor={updateColor}
                    style={styles.updateIcon}
                  />
                </View>

                <View style={styles.updateHeaderText}>
                  <Text variant='bodyMedium' style={styles.updateSkillName}>
                    {update.skillName}
                  </Text>
                  <Text variant='bodySmall' style={styles.updateSource}>
                    {getSourceLabel(update.source)}
                  </Text>
                </View>
              </View>

              <IconButton
                icon='close'
                size={16}
                onPress={() => handleUpdateDismiss(update.id)}
                style={styles.updateCloseButton}
              />
            </View>

            {/* Progress Change */}
            <View style={styles.progressChange}>
              <View style={styles.progressValues}>
                <Text variant='bodySmall' style={styles.oldProgress}>
                  {Math.round(update.oldProgress * 100)}%
                </Text>
                <Text style={styles.progressArrow}>→</Text>
                <Text
                  variant='bodyMedium'
                  style={[styles.newProgress, { color: updateColor }]}
                >
                  {Math.round(update.newProgress * 100)}%
                </Text>
              </View>

              <Text
                variant='bodySmall'
                style={[styles.improvement, { color: updateColor }]}
              >
                +{improvementPercent}% improvement
              </Text>
            </View>

            {/* Status Indicators */}
            <View style={styles.statusIndicators}>
              {update.optimistic && (
                <Chip
                  icon='clock-outline'
                  mode='outlined'
                  compact
                  style={styles.optimisticChip}
                  textStyle={styles.optimisticText}
                >
                  Pending
                </Chip>
              )}

              <Chip
                icon='brain'
                mode='outlined'
                compact
                style={[styles.confidenceChip, { borderColor: updateColor }]}
                textStyle={[styles.confidenceText, { color: updateColor }]}
              >
                {Math.round(update.confidence * 100)}% confident
              </Chip>
            </View>

            {/* Animated Progress Bar */}
            <View style={styles.miniProgressBar}>
              <View
                style={[
                  styles.miniProgressTrack,
                  { backgroundColor: theme.colors.surfaceVariant },
                ]}
              />
              <Animated.View
                style={[
                  styles.miniProgressFill,
                  {
                    width: animValue.interpolate({
                      inputRange: [0, 1],
                      outputRange: [
                        `${update.oldProgress * 100}%`,
                        `${update.newProgress * 100}%`,
                      ],
                    }),
                    backgroundColor: updateColor,
                  },
                ]}
              />
            </View>
          </Card.Content>
        </Card>
      </Animated.View>
    );
  };

  if (visibleUpdates.length === 0) return null;

  return (
    <View style={[styles.container, style]}>
      {visibleUpdates.map(renderProgressUpdate)}

      {/* Queue indicator */}
      {processingQueue.length > 0 && (
        <View style={styles.queueIndicator}>
          <Chip
            icon='clock-outline'
            mode='outlined'
            compact
            style={styles.queueChip}
            textStyle={styles.queueText}
          >
            {processingQueue.length} more update
            {processingQueue.length > 1 ? 's' : ''}
          </Chip>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    top: 120,
    right: 16,
    zIndex: 999,
    maxWidth: 280,
  },
  updateContainer: {
    marginBottom: 8,
  },
  updateCard: {
    elevation: 6,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
  },
  updateContent: {
    padding: 12,
  },
  updateHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  updateHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  updateIconContainer: {
    borderRadius: 16,
    marginRight: 8,
  },
  updateIcon: {
    margin: 0,
  },
  updateHeaderText: {
    flex: 1,
  },
  updateSkillName: {
    fontWeight: '600',
    marginBottom: 2,
  },
  updateSource: {
    opacity: 0.7,
    fontSize: 11,
  },
  updateCloseButton: {
    margin: 0,
  },
  progressChange: {
    marginBottom: 8,
  },
  progressValues: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 4,
  },
  oldProgress: {
    opacity: 0.7,
  },
  progressArrow: {
    marginHorizontal: 8,
    opacity: 0.6,
  },
  newProgress: {
    fontWeight: '600',
  },
  improvement: {
    textAlign: 'center',
    fontSize: 12,
    fontWeight: '500',
  },
  statusIndicators: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  optimisticChip: {
    backgroundColor: 'transparent',
    borderColor: '#FF9800',
  },
  optimisticText: {
    fontSize: 10,
    color: '#FF9800',
  },
  confidenceChip: {
    backgroundColor: 'transparent',
  },
  confidenceText: {
    fontSize: 10,
  },
  miniProgressBar: {
    height: 4,
    position: 'relative',
    borderRadius: 2,
    overflow: 'hidden',
  },
  miniProgressTrack: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  miniProgressFill: {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    borderRadius: 2,
  },
  queueIndicator: {
    alignItems: 'center',
    marginTop: 8,
  },
  queueChip: {
    backgroundColor: 'transparent',
    borderColor: '#9E9E9E',
  },
  queueText: {
    fontSize: 10,
    color: '#9E9E9E',
  },
});
